import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Bell, ArrowLeft, Sparkles, ArrowRight, MessageCircle, Mail,
  Share2, Copy, Check, BellRing, Smartphone, Zap,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/doctor-notify';
const SHARE_MESSAGE = (
  `Just saw this on DocSite — the moment a patient finishes your Connect link, ` +
  `your phone buzzes instantly with their name, mobile and which clinic they came from. ` +
  `One tap opens your dashboard.\n\n` +
  `If you haven't installed DocSite to your phone yet, you also get a clean email backup ` +
  `so a real patient is never missed.\n\n` +
  `1-pager: ${SHARE_URL}`
);

/**
 * Sharp, small 1-pager announcing the "doctor gets notified on new Connect"
 * launch. Doctor-first language — no slugs, no IDs, no "push subscription" jargon.
 */
export default function DoctorNotifyLaunch() {
  const [copied, setCopied] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("DocSite — get pinged the moment a patient connects")}&body=${encodeURIComponent(SHARE_MESSAGE)}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — instant patient ping', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Get pinged the moment a new patient connects</title>
        <meta name="description" content="When a patient finishes your DocSite Connect link, your phone buzzes instantly with their name, mobile and clinic. One tap opens your dashboard. Email backup included." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="doctor instant notification, patient lead alert, real-time doctor ping, DocSite Connect notification, doctor app India" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — Get pinged the moment a new patient connects" />
        <meta property="og:description" content="Instant ping. Name, mobile, clinic. One tap to your dashboard." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-07" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DocSite — Get pinged the moment a new patient connects" />
        <meta name="twitter:description" content="Instant ping. Name, mobile, clinic. One tap to your dashboard." />
        <link rel="canonical" href={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: 'Get pinged the moment a new patient connects',
          description: 'Instant phone ping when a patient reaches you on your DocSite Connect link. Name, mobile, clinic — one tap to your dashboard.',
          datePublished: '2026-02-07',
          dateModified: '2026-02-07',
          author: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
          publisher: {
            '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care',
            logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' },
          },
          mainEntityOfPage: SHARE_URL,
          keywords: 'doctor notification, patient lead, real-time alert, DocSite Connect',
        })}</script>
      </Helmet>

      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <Link to="/whats-new" data-testid="doctor-notify-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} /> All updates
        </Link>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Hero band */}
          <div className="bg-gradient-to-br from-sky-600 via-sky-500 to-emerald-500 text-white p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles size={14} /> Just Launched · 7 Feb 2026
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get pinged the moment a new patient connects
            </h1>
            <p className="text-sky-50 mt-3 text-sm sm:text-base leading-relaxed">
              Your phone now buzzes the instant a patient finishes your Connect link.
              Name, mobile, clinic — one tap opens your dashboard.
            </p>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8 space-y-6">
            {/* The 3 things doctors get */}
            <div className="grid sm:grid-cols-3 gap-3">
              <FeaturePill
                icon={<BellRing size={18} className="text-sky-600" />}
                title="Instant ping"
                body="Phone buzzes the second a patient connects."
                testid="doctor-notify-pill-ping"
              />
              <FeaturePill
                icon={<Smartphone size={18} className="text-emerald-600" />}
                title="One tap"
                body="Opens your dashboard with the new patient."
                testid="doctor-notify-pill-onetap"
              />
              <FeaturePill
                icon={<Mail size={18} className="text-amber-600" />}
                title="Email backup"
                body="Haven't installed DocSite yet? You still get an email."
                testid="doctor-notify-pill-email"
              />
            </div>

            {/* What you'll see */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">What you'll see on your phone</p>
              <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
                <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                  <Bell size={18} className="text-sky-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">New patient just connected</p>
                  <p className="text-xs text-slate-600 mt-0.5">Priya Sharma (●●●●●● 4231) at Andheri Clinic · Tap to see full details</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">Mobile is masked here for privacy. Full number is available inside your dashboard.</p>
            </div>

            {/* How to switch it on */}
            <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-2">To switch this on (30 seconds)</p>
              <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
                <li>Open DocSite on your phone's browser, tap the menu, and choose <span className="font-semibold">"Add to Home Screen" / "Install app"</span>.</li>
                <li>Open the app once and allow notifications when asked.</li>
                <li>That's it — every new patient connect now reaches your phone instantly.</li>
              </ol>
              <p className="text-xs text-amber-800 mt-3">No installs yet? An email lands at your registered address as a clean backup, so a real patient is never missed.</p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/dashboard" data-testid="doctor-notify-cta-dashboard"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm shadow-sm">
                Open my dashboard <ArrowRight size={16} />
              </Link>
              <Link to="/whats-new" data-testid="doctor-notify-cta-allupdates"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm">
                See all updates
              </Link>
            </div>

            {/* Share */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Tell a colleague</p>
              <div className="flex flex-wrap gap-2">
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="doctor-notify-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={mailHref} data-testid="doctor-notify-share-email"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200">
                  <Mail size={14} /> Email
                </a>
                <button onClick={nativeShare} data-testid="doctor-notify-share-native"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={copyLink} data-testid="doctor-notify-share-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          <Zap size={12} className="inline mr-1" />Free on every plan, including Gold (Free).
        </p>
      </div>
    </div>
  );
}

function FeaturePill({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">{icon}<p className="text-sm font-semibold text-slate-900">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
